import { useEffect, useRef, useState } from "react";
import { Microphone, MicrophoneSlash, CircleNotch } from "@phosphor-icons/react";

/**
 * Botón de dictado por voz que usa la Web Speech API del navegador.
 * Inserta el texto reconocido al final del valor actual mediante onAppend.
 *
 * Soporte: Chrome, Edge, Safari (no Firefox a día de hoy).
 */
export default function VoiceDictateButton({
  onAppend,
  lang = "es-ES",
  disabled = false,
  className = "",
  testid = "voice-dictate-button",
}) {
  const [supported, setSupported] = useState(false);
  const [listening, setListening] = useState(false);
  const [error, setError] = useState("");
  const recognitionRef = useRef(null);
  const finalAppendedRef = useRef(""); // para evitar duplicados con resultados parciales

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setSupported(false);
      return;
    }
    setSupported(true);
    const rec = new SpeechRecognition();
    rec.lang = lang;
    rec.continuous = true;
    rec.interimResults = false; // solo finales para evitar duplicados al insertar

    rec.onresult = (event) => {
      let chunk = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const r = event.results[i];
        if (r.isFinal) {
          chunk += r[0].transcript;
        }
      }
      const trimmed = chunk.trim();
      if (trimmed && trimmed !== finalAppendedRef.current) {
        finalAppendedRef.current = trimmed;
        onAppend?.(trimmed);
      }
    };

    rec.onerror = (e) => {
      if (e.error === "no-speech" || e.error === "aborted") return;
      if (e.error === "not-allowed" || e.error === "service-not-allowed") {
        setError("Permiso de micrófono denegado. Revisa la configuración del navegador.");
      } else {
        setError(`Error de dictado: ${e.error}`);
      }
      setListening(false);
    };

    rec.onend = () => {
      setListening(false);
      finalAppendedRef.current = "";
    };

    recognitionRef.current = rec;
    return () => {
      try { rec.stop(); } catch { /* noop */ }
      recognitionRef.current = null;
    };
  }, [lang, onAppend]);

  const toggle = () => {
    if (!recognitionRef.current) return;
    setError("");
    if (listening) {
      try { recognitionRef.current.stop(); } catch { /* noop */ }
      setListening(false);
    } else {
      try {
        recognitionRef.current.start();
        setListening(true);
      } catch (e) {
        // ya estaba activo o algún otro estado raro
        setError("No se pudo iniciar el dictado.");
      }
    }
  };

  if (!supported) {
    return (
      <button
        type="button"
        disabled
        title="Tu navegador no soporta dictado por voz. Prueba con Chrome, Edge o Safari."
        data-testid={`${testid}-unsupported`}
        className={`text-[11px] inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-border bg-surface text-muted/60 cursor-not-allowed ${className}`}
      >
        <MicrophoneSlash className="w-3.5 h-3.5" weight="bold" />
        Dictado no disponible
      </button>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={toggle}
        disabled={disabled}
        data-testid={testid}
        title={listening ? "Detener dictado" : "Dictar al campo"}
        className={`text-[11px] inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border font-semibold transition-colors disabled:opacity-50 ${
          listening
            ? "bg-red-50 border-red-300 text-red-700 hover:bg-red-100"
            : "bg-white border-border text-muted hover:text-foreground hover:border-brand/40"
        } ${className}`}
      >
        {listening ? (
          <>
            <CircleNotch className="w-3.5 h-3.5 animate-spin" weight="bold" />
            <span className="inline-flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
              Escuchando...
            </span>
          </>
        ) : (
          <>
            <Microphone className="w-3.5 h-3.5" weight="bold" />
            Dictar
          </>
        )}
      </button>
      {error && <span className="text-[10.5px] text-status-error-fg">{error}</span>}
    </div>
  );
}
